import { create } from 'zustand';
import { Platform } from 'react-native';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Child } from '../api/children';
import { SubscriptionState } from '../api/subscriptions';

type AuthStore = {
  accessToken: string | null;
  refreshToken: string | null;
  parentId: string | null;
  // TV-only: the device id assigned by the backend on /v1/pairing/exchange.
  // Passed as `?tvId=` on the WebSocket handshake so the gateway can bucket
  // sockets per-device and the parent can target a single TV with stop /
  // pause / forced_unpair. null on the parent stack and on legacy TVs that
  // paired before the multi-TV rollout.
  tvId: string | null;
  activeChild: Child | null;
  isAuthenticated: boolean;
  // On mobile, Kidly is kid-first: the phone defaults to kid mode and parent
  // mode is the PIN-gated detour ("Settings"). Intentionally NOT persisted
  // so every cold launch returns to kid mode — a kid who closes/reopens the
  // app can't land in the parent dashboard. The TV flow ignores this flag
  // entirely (it has its own profile picker via ProfileSelectScreen).
  kidMode: boolean;
  // Whether QuestionOverlay should read the question prompt aloud via the
  // browser SpeechSynthesis API. TV-first feature (Chromium 76+ supports
  // it natively); native iOS/Android gracefully no-op. Persisted so the
  // parent's choice survives launches. Default true — most kids benefit
  // from hearing the prompt at age band 3-6 even if they can read.
  ttsEnabled: boolean;
  // Last-known subscription snapshot, hydrated from /v1/subscriptions/me on
  // app start and refreshed after returning from Stripe Checkout/Portal.
  // Null until the first fetch resolves (treat as "not entitled" — the
  // paywall is safe-by-default). Persisted across refreshes so the FE
  // doesn't flash the upgrade CTA at people who already paid.
  subscription: SubscriptionState | null;
  _hydrated: boolean;
  setTokens: (
    accessToken: string,
    refreshToken: string,
    parentId?: string,
    tvId?: string,
  ) => void;
  setTvId: (tvId: string | null) => void;
  setParentId: (parentId: string) => void;
  setActiveChild: (child: Child | null) => void;
  setKidMode: (on: boolean) => void;
  setTtsEnabled: (on: boolean) => void;
  setSubscription: (s: SubscriptionState | null) => void;
  logout: () => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

// On native, `typeof window !== 'undefined'` is true (RN polyfills window)
// but localStorage is unavailable, so the previous factory silently no-op'd
// and the auth state was lost on every reload. Pick storage by platform.
const authStorage: StateStorage =
  Platform.OS === 'web'
    ? typeof window !== 'undefined' && window.localStorage
      ? (window.localStorage as unknown as StateStorage)
      : noopStorage
    : (AsyncStorage as unknown as StateStorage);

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      parentId: null,
      tvId: null,
      activeChild: null,
      isAuthenticated: false,
      kidMode: true,
      ttsEnabled: true,
      subscription: null,
      _hydrated: false,

      setTokens: (accessToken, refreshToken, parentId, tvId) =>
        set((prev) => ({
          accessToken,
          refreshToken,
          isAuthenticated: true,
          parentId: parentId ?? prev.parentId,
          // Only overwrite tvId when the caller provides one. The parent
          // login/signup flow passes nothing (parent never has a tvId),
          // while the TV's pairing-exchange path passes the new device id.
          tvId: tvId ?? prev.tvId,
        })),

      setParentId: (parentId) => set({ parentId }),

      setTvId: (tvId) => set({ tvId }),

      setActiveChild: (activeChild) => set({ activeChild }),

      setKidMode: (kidMode) => set({ kidMode }),

      setTtsEnabled: (ttsEnabled) => set({ ttsEnabled }),

      setSubscription: (subscription) => set({ subscription }),

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          parentId: null,
          tvId: null,
          activeChild: null,
          isAuthenticated: false,
          // Logout returns to the kid-first default so the next signed-in
          // session lands in kid mode again.
          kidMode: true,
          // Drop the cached subscription — the next account that signs in
          // shouldn't see the previous account's entitlement (would let a
          // shared device leak Premium access to a non-paying signup).
          subscription: null,
        });
      },
    }),
    {
      name: 'kidly-auth',
      storage: createJSONStorage(() => authStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        parentId: state.parentId,
        // Persist tvId on the TV so re-launching keeps it bucketed under
        // the same device on the gateway. Parents never have a tvId so
        // this is a no-op on their stack.
        tvId: state.tvId,
        isAuthenticated: state.isAuthenticated,
        subscription: state.subscription,
        // Persist `ttsEnabled` so the parent's settings choice survives
        // app launches. `kidMode` is intentionally excluded (see comment
        // above) — every cold start returns to kid mode.
        ttsEnabled: state.ttsEnabled,
      }),
      onRehydrateStorage: () => () => {
        useAuthStore.setState({ _hydrated: true });
      },
    },
  ),
);
